"use client";

import { usePathname } from "next/navigation";

import { maxComboOptionInstruments } from "#/config/combo";
import { routes } from "#/config/routes";
import { useComboInstrumentsController } from "#/hooks/useComboInstruments.controller";
import { useMarket } from "#/store/app/portfolio/market";
import { useComboActiveInstrumentId } from "#/store/combo/comboActiveInstrumentId";
import type { ComboInstrument } from "#/store/combo/comboInstruments";
import { useComboType } from "#/store/combo/comboType";
import { useMaturity } from "#/store/options/page/maturity";

export const useComboContentController = () => {
  const pathname = usePathname();
  const { market } = useMarket();
  const { isComboRFQ } = useComboType();
  const { setMaturity } = useMaturity();
  const { comboActiveInstrumentId, setComboActiveInstrumentId } =
    useComboActiveInstrumentId();
  const {
    comboPerpInstruments,
    comboOptionInstruments,
    handleAddLeg: defaultHandleAddLeg,
    handleDelete,
    handleAddPerp,
  } = useComboInstrumentsController();

  const comboPerpInstrument = comboPerpInstruments.at(0);
  const isOnComboPage = pathname === routes.combo;
  const isOnOptionsPage = pathname === routes.options();
  const isOnComboOrOptionsPage = isOnComboPage || isOnOptionsPage;
  const isInAddingNewLegState =
    isOnOptionsPage &&
    !comboActiveInstrumentId &&
    comboOptionInstruments.length < maxComboOptionInstruments;
  const disableNewLegsButton =
    comboOptionInstruments.length >= maxComboOptionInstruments ||
    !isOnComboOrOptionsPage;
  const disablePerpButton =
    Boolean(comboPerpInstrument) || !isOnComboOrOptionsPage || isComboRFQ;

  const handleAddLeg = () => {
    if (!isOnOptionsPage) {
      defaultHandleAddLeg();
      return;
    }

    const lastInstrumentId = comboOptionInstruments.at(-1)?.id ?? "";
    setComboActiveInstrumentId((previous) =>
      previous ? "" : lastInstrumentId,
    );
  };

  const handleSelectInstrument = (instrument: ComboInstrument) => {
    setComboActiveInstrumentId(instrument.id);
    setMaturity(instrument.maturity);
  };

  return {
    market,
    routes,
    isComboRFQ,
    isOnComboPage,
    isOnOptionsPage,
    comboPerpInstrument,
    disablePerpButton,
    disableNewLegsButton,
    isInAddingNewLegState,
    comboOptionInstruments,
    comboActiveInstrumentId,
    maxComboOptionInstruments,
    handleAddLeg,
    handleDelete,
    handleAddPerp,
    handleSelectInstrument,
  };
};
